import React, { useEffect, useState } from "react";
import BasicCustomTable from "../../../component/basicTable/index";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../configuration/request";
import { useSnackbar } from "../../../component/SnackbarContext";
import { useDispatch } from "react-redux";
import { setIncidentType } from "../../../Redux/Slices/counterSlice";
import { CircularProgress } from "@mui/material";
import { BsBugFill } from "react-icons/bs";
import {
  formatIncidentStatusLabel,
  INCIDENT_STATUSES_ALL,
  INCIDENT_STATUS_VALUES,
} from "./incidentStatus";

const columnsIncidents = [
  { field: "incidentId", label: "Incident ID" },
  { field: "title", label: "Title" },
  { field: "driverName", label: "Delivery Person" },
  { field: "vehicleNumber", label: "Vehicle #" },
  { field: "date", label: "Date" },
  { field: "status", label: "Status" },
  { field: "actions", label: "Actions" },
];

const getDefaultDates = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);
  return {
    from: from.toISOString().split("T")[0],
    to: to.toISOString().split("T")[0],
  };
};

function IncidentReports() {
  const defaults = getDefaultDates();
  const [incidents, setIncidents] = useState([]);
  const [columns] = useState(columnsIncidents);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();

  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [statusFilter, setStatusFilter] = useState(INCIDENT_STATUSES_ALL);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchIncidents();
  }, [dateFrom, dateTo, statusFilter]);

  const fetchIncidents = async () => {
    setLoading(true);
    const params = new URLSearchParams({
      dateFrom,
      dateTo,
      status: statusFilter,
    });

    try {
      const response = await axiosInstance.get(`/customer/delivery-person/incidents?${params}`);
      const payload = response?.data;
      // Paginated shape: { data: [...], total, page, limit, totalPages } — or legacy array body
      const rawIncidents = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      const formattedIncidents = rawIncidents.map((item, index) => {
        const dp = item?.deliveryPerson || {};
        const user = dp?.user || {};
        const driverName =
          user?.name?.trim() ||
          (dp?.id != null ? `Driver #${dp.id}` : "N/A");
        const vehicleNumber = dp?.vehicle_number?.trim() || "N/A";

        const created = item?.createdAt || item?.created_at;

        return {
          id: item?.id?.toString() || `temp-${index}`,
          incidentId: item?.id?.toString() || "",
          title: item?.title || "Untitled Incident",
          driverName,
          vehicleNumber,
          date: created
            ? new Date(created).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "N/A",
          status: formatIncidentStatusLabel(item?.status),
          actions: true,
          fullDetails: item,
        };
      });

      setIncidents(formattedIncidents);
    } catch (error) {
      console.error("Error fetching incidents:", error);
      showSnackbar("Failed to fetch incidents!", "error");
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (row) => {
    dispatch(setIncidentType("delivery_person"));
    navigate(`/app/delivery-incedents/${row.incidentId}`, {
      state: { incident: row.fullDetails },
    });
  };

  const handleDateRangeChange = (type, value) => {
    if (type === "from") setDateFrom(value);
    else setDateTo(value);
  };

  const resetFilters = () => {
    const { from, to } = getDefaultDates();
    setDateFrom(from);
    setDateTo(to);
    setStatusFilter(INCIDENT_STATUSES_ALL);
  };

  return (
    <div className="px-4 md:px-6">
      <div className="flex flex-col gap-4 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[var(--table-header-bg)] text-[var(--primary-color)]">
              <BsBugFill className="text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-[var(--primary-text-color)]">
                Incident Reports
              </h1>
              <p className="text-sm text-[var(--light-text-color)]">
                View and manage delivery incidents
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--table-border)] bg-[var(--primary-bg-color)] p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-sm font-medium text-[var(--label-color)] mb-1.5">
                From Date
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => handleDateRangeChange("from", e.target.value)}
                className="w-full border border-[var(--table-border)] rounded-lg px-3 py-2.5 text-[var(--primary-text-color)] bg-[var(--fields-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-sm font-medium text-[var(--label-color)] mb-1.5">
                To Date
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => handleDateRangeChange("to", e.target.value)}
                className="w-full border border-[var(--table-border)] rounded-lg px-3 py-2.5 text-[var(--primary-text-color)] bg-[var(--fields-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
              />
            </div>
            <div className="min-w-[160px]">
              <label className="block text-sm font-medium text-[var(--label-color)] mb-1.5">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-[var(--table-border)] rounded-lg px-3 py-2.5 text-[var(--primary-text-color)] bg-[var(--fields-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
              >
                <option value={INCIDENT_STATUSES_ALL}>All</option>
                {INCIDENT_STATUS_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {formatIncidentStatusLabel(v)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={fetchIncidents}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--primary-color)] text-white px-5 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Apply"
                )}
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center justify-center rounded-lg border border-[var(--table-border)] text-[var(--primary-text-color)] px-5 py-2.5 text-sm font-medium hover:bg-[var(--card-color)] transition"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden">
          {loading && incidents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-[var(--primary-bg-color)] border border-[var(--table-border)] rounded-2xl">
              <CircularProgress size={48} sx={{ color: "var(--primary-color)" }} />
              <p className="mt-3 text-sm text-[var(--light-text-color)]">Loading incidents...</p>
            </div>
          ) : (
            <BasicCustomTable
              columns={columns}
              rows={incidents}
              onView={handleView}
              colorHeader={true}
              customClassHeader="table-header-cell-colored"
              tableName="Incidents"
              maxHeightClass="max-h-[calc(100vh-380px)]"
              frontendPagination={true}
              pageLimit={10}
              emptyState="No incidents found"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default IncidentReports;
